// Story 7.8 (Twitch) + Story 7.20 (YouTube) — mutation hook for
// POST /api/v1/creators/me/aliases/<provider>/unlink.
//
// Idempotent server-side; invalidates the linked-accounts cache on
// success. Parameterized by provider so Kick (Story 7.21) needs no new
// hook — was `useUnlinkTwitch` with the path hardcoded.

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import { LINKED_ACCOUNTS_QUERY_KEY } from "./useLinkedAccounts";

export type AliasProvider = "twitch" | "youtube" | "kick";

export interface UnlinkAliasResponse {
  data: { unlinked: boolean };
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postUnlink(provider: AliasProvider): Promise<UnlinkAliasResponse> {
  const response = await fetch(
    `/api/v1/creators/me/aliases/${provider}/unlink`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        // The /creators/me idempotency prefix covers this POST — the
        // middleware 400s without a key.
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`unlink failed (HTTP ${response.status})`);
  }
  return (await response.json()) as UnlinkAliasResponse;
}

export function useUnlinkAlias(
  provider: AliasProvider,
): UseMutationResult<UnlinkAliasResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<UnlinkAliasResponse, Error, void>({
    mutationFn: () => postUnlink(provider),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: LINKED_ACCOUNTS_QUERY_KEY });
    },
  });
}
